import * as React from 'react';
import { Dropdown, DropdownItem, DropdownSeparator, DropdownToggle } from '@patternfly/react-core';
import { ThIcon } from '@patternfly/react-icons';
import UserInterface from '@app/Interface/UserInterface';
import UserAuthentication from '@app/Security/UserAuthentication';
import FpuPermissionsComponentTypeInterface from '@app/Interface/FpuPermissionsComponentViewInterface';
import FpuPermissionsComponentTypeModel from '@app/Model/FpuPermissionsComponentViewModel';
import UserFpuAccessHelper from '@app/Service/UserFpuAccessHelper';
import { ToastNotificationType } from '@app/Interface/ToastNotificationInterface';
import { CreateToastNotification, ToastNotificationContext } from '@app/Context/ToastNotificationContext';
import ProducerInterface from '@app/Interface/ProducerInterface';

interface UserPermissionButtonsProps {
    user: UserInterface;
    currentUser: UserInterface;
    producerId: number;
    producer: ProducerInterface;
    changeView: (component: FpuPermissionsComponentTypeInterface, fetchUsers?: boolean) => void;
}

/**
 * Generates a list stylized buttons with the role
 * 
 * @param UserPermissionButtonsProps
 * @returns 
 */
const UserPermissionButtons: React.FC<UserPermissionButtonsProps> = ({ user, currentUser, producerId, producer, changeView }) => {

    // NOTE: ToastNotificationContext provider was added to `src\app\index.tsx`
    const { toastDispatch } = React.useContext(ToastNotificationContext);

    const [isOpenActionItem, setIsOpenActionItem] = React.useState(false);

    const [actionItems, setActionItems] = React.useState<JSX.Element[]>([]);

    React.useEffect(() => {

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleResendInvite = async (event: MouseEvent | React.MouseEvent<any, MouseEvent> | React.KeyboardEvent<Element>) => {

            event.preventDefault();

            let notificationType: ToastNotificationType = ToastNotificationType.Default;

            let toastTitle = "";

            let toastDescription = "";

            // resend Invite
            const inviteSent = await UserFpuAccessHelper.resendInvite(user.email, producerId);

            if (inviteSent) {
                notificationType = ToastNotificationType.Success;
                toastTitle = "Success";
                toastDescription = "Invite was successfully resent";
            } else {
                notificationType = ToastNotificationType.Danger;
                toastTitle = "Error";
                toastDescription = "An error occurred resending the invite, please try again or contact support.";
            }

            CreateToastNotification(toastDispatch, notificationType, toastTitle, toastDescription);
        }

        const actions: JSX.Element[] = [];

        // make sure user can grant access for this fpu
        if (UserAuthentication.userCanGrantAccess(currentUser.viewPermissions, Number(producerId))) {

            actions.push(<DropdownItem key="changePermissions" onClick={() => changeView(new FpuPermissionsComponentTypeModel(producerId, producer, user.id, user, "FpuUpdateUserPermissions"))}>Change Permissions</DropdownItem>);
            actions.push(<DropdownItem key="revokeUser" onClick={() => changeView(new FpuPermissionsComponentTypeModel(producerId, producer, user.id, user, "FpuRevokeUserPermissions"))}>Revoke User</DropdownItem>);

            if (user.confirmed === false) {

                actions.push(<DropdownSeparator key="separator" />);
                actions.push(<DropdownItem key="resendInvite" onClick={(event) => handleResendInvite(event)}>Resend Invite</DropdownItem>);

            }

        }

        setActionItems(actions);


    }, [changeView, currentUser.viewPermissions, producer, producerId, toastDispatch, user, user.confirmed, user.email, user.id]);

    return (
        <Dropdown
            onSelect={() => { setIsOpenActionItem(!isOpenActionItem) }}
            isOpen={isOpenActionItem}
            isPlain
            dropdownItems={actionItems}
            direction="up"
            toggle={
                <DropdownToggle
                    toggleIndicator={null}
                    onToggle={(isOpen) => { setIsOpenActionItem(isOpen) }}
                    aria-label="User Actions"
                    id={`user-${user.id}-actions-toggle`}
                >
                    <ThIcon></ThIcon>
                </DropdownToggle>
            }
        />
    );

}

export default UserPermissionButtons;